import type { Speicher } from './belegnummern';
import type { Empfaenger } from './api';
import type { BonierStand } from './bonierung';
import type { Position } from './warenkorb';

/**
 * Offene Tische / geparkte Bons: Ein angefangener Bon wird unter einem Namen
 * (Tisch 3, Theke, Kunde Huber …) auf dem Gerät abgelegt und später wieder
 * aufgerufen. Geparkte Bons sind keine Belege – ein Beleg entsteht erst beim
 * Kassieren (Regel 3). Je Kasse in localStorage.
 */
export interface GeparkterBon {
  name: string;
  positionen: Position[];
  empfaenger?: Empfaenger;
  notiz?: string;
  /** Schon an Küche/Schank gesendet (Modul Küchenbons). */
  boniert?: BonierStand[];
  /** ISO-8601 */
  geparktAm: string;
}

function schluessel(kassenId: string): string {
  return `bilanx.tische.${kassenId}`;
}

function istBon(x: unknown): x is GeparkterBon {
  if (typeof x !== 'object' || x === null) {
    return false;
  }
  const b = x as Record<string, unknown>;
  return (
    typeof b.name === 'string' &&
    b.name.trim() !== '' &&
    Array.isArray(b.positionen) &&
    typeof b.geparktAm === 'string'
  );
}

export function ladeTische(speicher: Speicher, kassenId: string): GeparkterBon[] {
  const roh = speicher.getItem(schluessel(kassenId));
  if (roh === null) {
    return [];
  }
  try {
    const liste: unknown = JSON.parse(roh);
    return Array.isArray(liste) ? liste.filter(istBon) : [];
  } catch {
    return [];
  }
}

function speichere(speicher: Speicher, kassenId: string, liste: readonly GeparkterBon[]): void {
  speicher.setItem(schluessel(kassenId), JSON.stringify(liste));
}

/** Bon unter einem Namen parken; ein vorhandener Tisch gleichen Namens wird ersetzt. */
export function parkeBon(
  speicher: Speicher,
  kassenId: string,
  bon: Omit<GeparkterBon, 'geparktAm'>,
  jetzt = new Date(),
): GeparkterBon[] {
  const name = bon.name.trim();
  if (name === '' || bon.positionen.length === 0) {
    throw new RangeError('Tischname und mindestens eine Position nötig');
  }
  const neu: GeparkterBon = { ...bon, name, geparktAm: jetzt.toISOString() };
  const liste = [...ladeTische(speicher, kassenId).filter((t) => t.name !== name), neu];
  speichere(speicher, kassenId, liste);
  return liste;
}

/** Tisch aufrufen: liefert den Bon und entfernt ihn aus der Liste (null, wenn unbekannt). */
export function holeBon(speicher: Speicher, kassenId: string, name: string): GeparkterBon | null {
  const liste = ladeTische(speicher, kassenId);
  const bon = liste.find((t) => t.name === name) ?? null;
  if (bon !== null) {
    speichere(
      speicher,
      kassenId,
      liste.filter((t) => t.name !== name),
    );
  }
  return bon;
}

export function entferneTisch(speicher: Speicher, kassenId: string, name: string): GeparkterBon[] {
  const liste = ladeTische(speicher, kassenId).filter((t) => t.name !== name);
  speichere(speicher, kassenId, liste);
  return liste;
}

/** Nächster freier Standardname „Tisch n". */
export function naechsterTischname(liste: readonly GeparkterBon[]): string {
  for (let n = 1; ; n++) {
    const name = `Tisch ${String(n)}`;
    if (!liste.some((t) => t.name === name)) {
      return name;
    }
  }
}
